import React from 'react';
import { Badge, type BadgeTone } from './Badge';
import { cn } from './cn';

export type InspectionStatus = 'IN' | 'NI' | 'NP' | 'O' | 'pending';

interface StatusConfig {
  label: string;
  tone: BadgeTone;
  dot: boolean;
  dashed: boolean;
}

// Tone mapping tracks the 2-badge client palette: `warning` aliases to
// sev-major (red) in our token system, so NI uses `info` (sev-minor = amber)
// to match the inspector-mobile kit's --status-ni-bg (warn-50). Pending is
// brand-tinted dashed per the kit ("Inspect" pill).
const STATUS: Record<InspectionStatus, StatusConfig> = {
  IN:      { label: 'IN',         tone: 'success', dot: true,  dashed: false },
  NI:      { label: 'NI',         tone: 'info',    dot: true,  dashed: false },
  NP:      { label: 'NP',         tone: 'neutral', dot: false, dashed: true  },
  O:       { label: 'O',          tone: 'brand',   dot: true,  dashed: false },
  pending: { label: 'TO INSPECT', tone: 'brand',   dot: false, dashed: true  },
};

const LONG_LABEL: Record<InspectionStatus, string> = {
  IN:      'Inspected',
  NI:      'Not Inspected',
  NP:      'Not Present',
  O:       'Inspected (with findings)',
  pending: 'To inspect',
};

export interface StatusPillProps {
  status: InspectionStatus;
  /** Show the long label ("Inspected") instead of the short code ("IN"). */
  long?: boolean;
  compact?: boolean;
  className?: string;
}

export const StatusPill: React.FC<StatusPillProps> = ({ status, long = false, compact = false, className }) => {
  const cfg = STATUS[status];
  return (
    <Badge
      tone={cfg.tone}
      dot={cfg.dot}
      dashed={cfg.dashed}
      compact={compact}
      className={cn('uppercase tracking-wide', className)}
      aria-label={LONG_LABEL[status]}
    >
      {long ? LONG_LABEL[status] : cfg.label}
    </Badge>
  );
};
